import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { PortfolioItemsWithCalculation } from '@easy-lib/modules/portfolio/models/portfolio-item.model';
import { PortfolioTotalValueCalculateType } from '@easy-lib/modules/settings';
import {
	LsService,
	MarketDataService,
	SymbolService,
	TimeService,
	UserDeviceService
} from '@easy-lib/modules/shared/services';
import { Store } from '@ngrx/store';
import { combineLatestWith, distinctUntilChanged } from 'rxjs';
import { tap } from 'rxjs/operators';
import { SettingStore } from '../setting/setting.store';
import { SoldItemsAction } from '@easy-lib/state/sold-items/sold-items.action';
import { SoldItemsStore } from '@easy-lib/state/sold-items/sold-items.store';

@Injectable({
	providedIn: 'root'
})
export class SoldItemsFacade {
	private readonly store: Store = inject(Store);
	lsService: LsService = inject(LsService);
	marketDataService: MarketDataService = inject(MarketDataService);
	userDeviceService = inject(UserDeviceService);
	soldItemsStore = inject(SoldItemsStore);
	symbolService = inject(SymbolService);
	settingStore = inject(SettingStore);
	timeService = inject(TimeService);
	constructor() {
		toSignal(this.updateSoldItems$);
	}

	updateSoldItemsStore(values) {
		this.store.dispatch(SoldItemsAction.upsert({ values }));
	}

	getNeedToUpdateItems(storeItems: PortfolioItemsWithCalculation[], settings, symbols, isMarketOpen) {
		const needToUpdateItems = [];
		const deActiveSymbols = [];
		storeItems.forEach(item => {
			const symbol = symbols.find(symbol => symbol.symbolIsin === item.symbolIsin);
			let changes;
			if (!symbol) {
				deActiveSymbols.push(item.symbolIsin);
				changes = {
					portfolioTotalValueCalculateType: settings.portfolioTotalValueCalculateType,
					portfolioBuyAveragePriceCalculationType: settings.soldPortfolioBuyAveragePriceCalculationType,
					portfolioCalculationType: settings.soldPortfolioCalculationType,
					canBuy: false,
					canSell: false,
					buyCommission: 0,
					sellCommission: 0,
					symbolName: item.symbolIsin,
					title: item.symbolIsin,
					symbolIsin: item.symbolIsin
				};
			} else {
				changes = {
					portfolioTotalValueCalculateType: settings.portfolioTotalValueCalculateType,
					portfolioBuyAveragePriceCalculationType: settings.soldPortfolioBuyAveragePriceCalculationType,
					portfolioCalculationType: settings.soldPortfolioCalculationType,
					canBuy: symbol?.canBuy,
					canSell: symbol?.canSell,
					buyCommission: +symbol?.buyCommission,
					sellCommission: +symbol?.sellCommission,
					symbolName: symbol?.symbolName,
					title: symbol?.title,
					manner: symbol?.manner,
					isCaution: symbol?.isCaution,
					symbolIsin: item.symbolIsin
				};
			}
			if (settings.portfolioTotalValueCalculateType === PortfolioTotalValueCalculateType.Automatic) {
				changes.portfolioTotalValueCalculateType = isMarketOpen
					? PortfolioTotalValueCalculateType.LastTradedPrice
					: PortfolioTotalValueCalculateType.ClosePrice;
			}
			needToUpdateItems.push(changes);
		});
		if (deActiveSymbols.length > 0) this.symbolService.getSome(deActiveSymbols).subscribe();
		return needToUpdateItems;
	}

	updateSoldItems$ = this.settingStore.setting$.pipe(
		combineLatestWith(
			this.timeService.isMarketOpen$.pipe(distinctUntilChanged()),
			this.symbolService.getSymbolsStream(),
			this.soldItemsStore.getLastSoldInsertTime$.pipe(distinctUntilChanged())
		),
		tap(([settings, isMarketOpen, symbols]) => {
			if (this.soldItemsStore.portfolioSoldItems().filter(x => x.symbolIsin?.length > 5).length > 0)
				this.updateSoldItemsStore(
					this.getNeedToUpdateItems(
						this.soldItemsStore.portfolioSoldItems().filter(x => !!x.symbolIsin),
						settings,
						symbols,
						isMarketOpen
					)
				);
		})
	);
}
